/* engine/pricingSettings.js — TECHNICIAN-OWNED PLATFORM PRICING (2026-08-18
 * hardcode purge: "the pricing must come from what we put in the Cabinet
 * Technician").
 *
 * Every number here used to be a pure hardcode scattered through the engines:
 * the +12% assembly fee, the +20% custom-size surcharge, the G1–G9 price-group
 * multipliers, the €/m worktop/plinth/cornice default rates and the accessory
 * quantity cap. They now resolve from `blob.pricing.*` (Cabinet Technician →
 * Pricing page) with these literals ONLY as the fallback for a catalog that
 * has not authored them — the same default-with-override pattern as
 * engine/surfaces.js.
 *
 * TWO consumption paths:
 *   · resolvePricing(blobPricing)  — pure; used by everything that already
 *     holds the blob (buildQuoteLines, configOptionDeltas, the PY twin
 *     mirrors this in pricing_system_a._pricing_cfg).
 *   · setPricingConfig(blobPricing) + getPricing() — a module singleton for
 *     the callers that have no blob in their signature (pvModulePrice, the
 *     footer, mcvCalcPrice). Set at every app boot that fetches the catalog.
 *
 * PARITY: backend/pricing_system_a.py `_pricing_cfg` resolves the same fields
 * with the same defaults — JS and Python must never disagree on a cent.
 */

export const PRICING_DEFAULTS = {
  assemblyPct: 12,          // "Pre-assembled delivery" fee, % of cabinet lines
  customSizePct: 20,        // custom-size surcharge, % of the module base
  worktopRate: 180,         // €/m default worktop line (no rated material)
  plinthRate: 35,           // €/m plinth line
  corniceRate: 45,          // €/m cornice line (wall units)
  maxAccessoryQty: 10,      // interior-accessory quantity cap per entry
  pgMultipliers: { G1: 1.00, G2: 1.18, G3: 1.38, G4: 1.65, G5: 1.85, G6: 2.10, G7: 2.35, G8: 2.65, G9: 3.00 },
};

const pct = (v, dflt) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 500 ? n : dflt;   // 0 legitimately turns a fee OFF
};
const rate = (v, dflt) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : dflt;
};
const qty = (v, dflt) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 1 && n <= 99 ? n : dflt;
};

/** Pure resolution: technician value → platform default. */
export function resolvePricing(blobPricing) {
  const p = blobPricing || {};
  const mult = { ...PRICING_DEFAULTS.pgMultipliers };
  const pm = p.pgMultipliers;
  if (pm && typeof pm === "object") {
    Object.keys(mult).forEach((g) => {
      const n = Number(pm[g]);
      if (Number.isFinite(n) && n > 0 && n <= 20) mult[g] = n;
    });
  }
  return {
    assemblyPct: pct(p.assemblyPct, PRICING_DEFAULTS.assemblyPct),
    customSizePct: pct(p.customSizePct, PRICING_DEFAULTS.customSizePct),
    worktopRate: rate(p.worktopRate, PRICING_DEFAULTS.worktopRate),
    plinthRate: rate(p.plinthRate, PRICING_DEFAULTS.plinthRate),
    corniceRate: rate(p.corniceRate, PRICING_DEFAULTS.corniceRate),
    maxAccessoryQty: qty(p.maxAccessoryQty, PRICING_DEFAULTS.maxAccessoryQty),
    pgMultipliers: mult,
  };
}

/* ── the singleton for blob-less call sites ──────────────────────────────── */
let _cfg = resolvePricing(null);

/** Install the tenant's pricing settings (call wherever the catalog loads). */
export function setPricingConfig(blobPricing) {
  _cfg = resolvePricing(blobPricing);
  return _cfg;
}

export function getPricing() {
  return _cfg;
}

/** The price-group multiplier — replaces every direct PG_MULT[pg] read. */
export function pgMultOf(pg, cfg) {
  const c = cfg || _cfg;
  return c.pgMultipliers[pg] || 1.0;
}
